/** Console v0.5 Super admin: the words and the create-account form's rules (design brief v0.5 s.6-7; session A's
 * contracts s.13-14). Pure -- tested with Node (web/console/test/v05-admin.test.ts). The server checks everything
 * again (limits below use, the company's one organisation, the ABN checksum). */

export type AccountType = "practice" | "company";
export type Preset = "small" | "medium" | "large";
export type AccountFilter = "all" | "practice" | "company" | "setup";

/** Brief s.7 placeholder limits (configurable on the server; A's presets match). */
export const PRESETS: Record<AccountType, Record<Preset, { organisations: number; users: number; label: string }>> = {
  practice: {
    small: { organisations: 25, users: 10, label: "Small · up to 25 organisations" },
    medium: { organisations: 100, users: 20, label: "Medium · up to 100" },
    large: { organisations: 250, users: 50, label: "Large · 100+" },
  },
  company: {
    small: { organisations: 1, users: 5, label: "Small · up to 5 users" },
    medium: { organisations: 1, users: 25, label: "Medium · up to 25" },
    large: { organisations: 1, users: 50, label: "Large · 25+" },
  },
};

export const ADMIN_COPY = {
  intro:
    "Every practice and company on Preekie: create accounts, set how many organisations and users each may have, and see who has access. Client financial data isn't shown here.",
  create: "Create an account",
  createFootnote: "The admin gets an invite. The account goes live once payment is set up. Everyone must set up an authenticator app.",
  practiceHint: "Plan sets the defaults; you can change either limit. Prices are still to be decided.",
  companyHint: "A company is one organisation for now; raise it only for an approved group structure.",
  private: "ledgers, meetings and documents stay private to the account",
  created: "Account created. The admin invite has been sent. It goes live once payment is set up.",
  createdTestDomain: "Account created. Test address: no email was sent. Use the accept link below. It goes live once payment is set up.",
  createdNoInvite: "Account created, but the admin invite couldn't be sent. Use Resend admin invite.",
  abnHint: "Optional. Preekie checks it with ABN Lookup when you leave the field. Advice only: it never stops you creating the account.",
  abnCheck: "Check ABN",
  abnChecking: "Checking ABN Lookup…",
  abnInvalid: "That ABN isn't valid. Check the 11 digits.",
  abnNotFound: "Not found: ABN Lookup has no record of that ABN. Check the 11 digits.",
  abnRateLimited: "Too many lookups just now. Wait a minute, then press Check ABN.",
  abnUnavailable: "ABN Lookup isn't available just now. You can still go on without it.",
  abnFailed: "Preekie couldn't check the ABN just now. You can still go on without it.",
  abnNameDiffers: "The legal name above is different.",
  abnUseName: "Use this name",
  abnCancelled: (date: string | null): string =>
    `Check this ABN: it was cancelled${date ? ` on ${date}` : ""}. Confirm with the client before you create the account.`,
  expired: "You were signed out after 15 minutes without activity. Sign in again.",
  noPlanHeading: "Accounts without a plan",
  noPlanLine: "This account can't be listed or limited until it has a plan.",
  noPlanLabel: "No plan",
  noPlanButton: "Set up plan",
  noPlanSave: "Save plan",
  noPlanSaved: "The plan is saved. The account is now in the list.",
  noPlanFailed: "The plan wasn't saved. Try again.",
  noPlanTitle: (name: string): string => `Set up a plan for ${name}`,
  /** The button's accessible name contains its visible label (WCAG 2.5.3): "Set up plan for <name>". */
  noPlanAriaLabel: (name: string): string => `Set up plan for ${name}`,
} as const;

export function statusLabel(status: string): string {
  return status === "active" ? "Active" : status === "pending_payment" ? "Awaiting payment" : status === "suspended" ? "Suspended" : status;
}

export function matchesFilter(a: { type: AccountType; plan: { status: string } }, f: AccountFilter): boolean {
  return f === "all" || (f === "setup" ? a.plan.status !== "active" : a.type === f);
}

export function matchesSearch(a: { name: string; abn?: string | null }, q: string): boolean {
  const s = q.trim().toLowerCase();
  if (!s) return true;
  const digits = s.replace(/\s/g, "");
  return a.name.toLowerCase().includes(s) || (!!a.abn && /^\d+$/.test(digits) && a.abn.replace(/\s/g, "").includes(digits));
}

/** ABN: 11 digits with the ATO checksum (weights 10,1,3,...,19; first digit less 1; sum divisible by 89). */
export function abnValid(abn: string): boolean {
  const d = abn.replace(/\s/g, "");
  if (!/^\d{11}$/.test(d)) return false;
  const w = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
  const sum = d.split("").reduce((acc, ch, i) => acc + (Number(ch) - (i === 0 ? 1 : 0)) * w[i]!, 0);
  return sum % 89 === 0;
}

export interface CreateForm {
  type: AccountType;
  name: string;
  abn: string;
  adminEmail: string;
  preset: Preset;
  organisations: number;
  users: number;
}

export function createProblem(f: CreateForm): string | null {
  if (!f.name.trim()) return "Enter the account's legal name.";
  if (f.abn.trim() && !abnValid(f.abn)) return "That ABN isn't valid. Check the 11 digits, or leave it blank.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.adminEmail.trim())) return "Enter the account admin's email address.";
  return planProblem(f);
}

export interface PlanForm {
  type: AccountType;
  preset: Preset;
  organisations: number;
  users: number;
}

/** The plan and limits fields, shared by the create-account form and the set-up-plan dialog. */
export function planProblem(f: PlanForm): string | null {
  if (!Number.isInteger(f.organisations) || f.organisations < 1) return "Organisations allowed must be a whole number, 1 or more.";
  if (!Number.isInteger(f.users) || f.users < 1) return "Users allowed must be a whole number, 1 or more.";
  if (f.type === "company" && f.organisations !== 1) return "A company account has exactly 1 organisation.";
  return null;
}

export interface PlanBody {
  plan_type: AccountType;
  preset: Preset;
  max_organisations: number;
  max_users: number;
}

/** The POST body for an account with no plan row: all four named, because the server creates the row from exactly what it is told. */
export function planBody(f: PlanForm): PlanBody {
  return { plan_type: f.type, preset: f.preset, max_organisations: f.organisations, max_users: f.users };
}

/** "Accounts without a plan" shows only when the server names one; otherwise it isn't drawn at all. */
export function showNoPlanPanel(list: ReadonlyArray<unknown> | null | undefined): boolean {
  return !!list && list.length > 0;
}

/** BR-0037: what ABN Lookup said (GET /api/admin/abn-lookup). */
export interface AbnFound {
  result: "found";
  kind: "abn" | "acn";
  number: string;
  abn: string | null;
  acn: string | null;
  name: string;
  status: { value: "active" | "cancelled"; from: string | null };
  gst: { registered: boolean; from: string | null };
  entity_type: { code: string; description: string };
}

export interface AbnNotFound {
  result: "not_found";
  kind: "abn" | "acn";
  number: string;
}

/** The lookup's outcome: the server's answer, or its error code (null when there was none, e.g. no connection) and message. */
export type AbnOutcome = { answer: AbnFound | AbnNotFound } | { error: string | null; message?: string };

/** What the dialog shows. Advice only: none of it blocks Create account. Not found reads as a warning;
 * unavailable and other failures read as muted "go on without it" lines, so the two never look alike. */
export type AbnView =
  | {
      state: "found";
      name: string;
      abn: string | null;
      entity: string;
      active: boolean;
      statusFrom: string | null;
      gstRegistered: boolean;
      gstFrom: string | null;
      cancelled: string | null;
    }
  | { state: "not_found" | "invalid"; text: string; warn: true }
  | { state: "rate_limited" | "unavailable" | "failed"; text: string; warn: false };

/** "2016-09-12" as 12/09/2016; anything else (or nothing) as null. */
export function abnDate(iso: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : null;
}

export function abnView(o: AbnOutcome): AbnView {
  if ("answer" in o) {
    const a = o.answer;
    if (a.result === "not_found") return { state: "not_found", text: ADMIN_COPY.abnNotFound, warn: true };
    const active = a.status.value === "active";
    const from = abnDate(a.status.from);
    return {
      state: "found",
      name: a.name,
      abn: a.abn,
      entity: a.entity_type.description,
      active,
      statusFrom: from,
      gstRegistered: a.gst.registered,
      gstFrom: abnDate(a.gst.from),
      cancelled: active ? null : ADMIN_COPY.abnCancelled(from),
    };
  }
  if (o.error === "invalid_number") return { state: "invalid", text: ADMIN_COPY.abnInvalid, warn: true };
  if (o.error === "rate_limited") return { state: "rate_limited", text: ADMIN_COPY.abnRateLimited, warn: false };
  if (o.error === "lookup_unavailable") return { state: "unavailable", text: o.message || ADMIN_COPY.abnUnavailable, warn: false };
  return { state: "failed", text: ADMIN_COPY.abnFailed, warn: false };
}

/** Whether the typed legal name differs from the registered one (case and spacing ignored); an empty field differs. */
export function abnNameDiffers(typed: string, registered: string): boolean {
  const norm = (s: string) => s.trim().replace(/\s+/g, " ").toLowerCase();
  return norm(typed) !== norm(registered);
}

/** The POST body: limits only when they differ from the preset (the server applies the preset otherwise). */
export function createBody(f: CreateForm): {
  name: string;
  type: AccountType;
  preset: Preset;
  abn?: string;
  max_organisations?: number;
  max_users?: number;
  admin_email: string;
} {
  const p = PRESETS[f.type][f.preset];
  const body: ReturnType<typeof createBody> = { name: f.name.trim(), type: f.type, preset: f.preset, admin_email: f.adminEmail.trim() };
  if (f.abn.trim()) body.abn = f.abn.replace(/\s/g, "");
  if (f.organisations !== p.organisations) body.max_organisations = f.organisations;
  if (f.users !== p.users) body.max_users = f.users;
  return body;
}

export function presetName(preset: string): string {
  return preset ? preset.charAt(0).toUpperCase() + preset.slice(1) : "";
}
